import {Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, Watch} from "@stencil/core";
import {NavItemTypeEnum} from "../../../core/nav/domain/NavItemTypeEnum";
import {NavUtils} from "../../../core/nav/utils/NavUtils";
import {WebUtils} from "../../../core/utils/WebUtils";
import {StringUtils} from "../../../core/utils/StringUtils";

@Component({
    tag: 'snt-nav-item',
    styleUrl: "nav-item-component.scss",
    shadow: false
})
export class NavItemComponent {

    public static readonly TAG_NAME = "snt-nav-item";

    @Element() element!: HTMLSntNavItemElement;

    @Prop({reflect: true}) href:string;
    @Prop({reflect: true}) target:string;
    @Prop({reflect: true}) linkTitle:string;
    @Prop() tabIdx:number;

    @Prop({reflect: true}) iconClassName:string;
    @Prop({reflect: true}) expanded:boolean = false;
    @Prop({reflect: true}) current:boolean = false;
    @Prop({reflect: true}) styleClasses:string = StringUtils.empty();
    @Prop() isExternal: boolean = false;

    @Prop() emitHoverEvents: boolean = false;
    @Prop() hasExpander: boolean = false;
    @Prop() isTopNavLink: boolean = false;

    @Event() navItemEvent:EventEmitter;
    @Event() navHeaderHover:EventEmitter;
    @Event() navHeaderLeave:EventEmitter;

    private parent:HTMLSntNavItemElement;
    private type:NavItemTypeEnum;

    @Method()
    async expand() {
        this.expanded = true;
    }

    @Method()
    async collapse() {
        this.expanded = false;
    }

    @Method()
    async setCurrent() {
        this.current = true;
    }

    @Method()
    async unsetCurrent() {
        this.current = false;
    }

    @Method()
    async toggleCurrent() {
        this.current = !this.current;

    }

    @Watch("current")
    watchCurrent() {
        if(this.parent) {
            this.parent.toggleCurrent().then(null);
            if(!(window.innerWidth >= 768)) {
                this.parent.expanded = true;
            }
        }
    }

    @Watch("expanded")
    watchExpanded() {
        if(this.type == NavItemTypeEnum.SUBNAV) {
            this.navItemEvent.emit();
        }
    }

    @Method()
    async registerParent(parent:HTMLSntNavItemElement) {
        this.parent = parent;
    }

    @Listen("mouseover")
    handleMouseOver(_e) {
        this.emitHoverEvents && this.navHeaderHover.emit(this.tabIdx);
    }

    @Listen("mouseleave")
    handleMouseleave() {
        this.emitHoverEvents && this.navHeaderLeave.emit();
    }

    @Listen('linkExpandToggle')
    handleLinkExpandToggle() {
        console.log('got link toggle event from nav item');
        
    }

    componentWillLoad() {
        this.type = NavUtils.identifyType(this.element);
    }

    componentDidLoad() {
        this.registerChildren();
    }

    @Method()
    async isType(type:NavItemTypeEnum) {
        return type == this.type;
    }

    private registerChildren() {
        if(this.type == NavItemTypeEnum.SUBNAV) {
            const children = this.element.querySelectorAll(NavItemComponent.TAG_NAME);
            if(children) {
                children.forEach((child)=> {
                    child.registerParent(this.element);
                })
            }
        }
    }

    render() {
        const subnavId = `${this.element.id}-subnav`;
        const showClass = (this.expanded) ? "show" : "";
        const navItemClass = ["nav-item"];
        (this.type == NavItemTypeEnum.SUBNAV) ? navItemClass.push(`dropdown ${showClass}`) : false;

        const navLinkClass = `nav-link ${(this.type == NavItemTypeEnum.SUBNAV) ? "dropdown-toggle" : ""}`;
        const linkAriaLabel = `${(this.type == NavItemTypeEnum.SUBNAV) ? `${this.linkTitle}, tab to the next button to expand the sub-menu` : this.linkTitle}`;
        //const ariaCurrent = `${(this.current) ? "page" : false}`;

        const icon = [
            <snt-icon styleClasses={this.iconClassName} />
        ]

        const link = [
            <snt-link
                href={this.href}
                target={this.target}
                class={navLinkClass}
                current={this.current}
                aria-label={linkAriaLabel}
                aria-current={true}
                hidden-title={this.linkTitle}
                is-external={this.isExternal}
                has-expander={this.hasExpander}
                for-tab={this.tabIdx}
                isTopNavLink={this.isTopNavLink}
            >
                {this.iconClassName ? icon : ""}
                {this.linkTitle}
            </snt-link>
        ];


        const expander = (
            <button
                type="button"
                class="expander"
                aria-expanded={this.expanded.toString()}
                aria-haspopup="true"
                aria-label={`${this.linkTitle}`}
                aria-owns={subnavId}
            >
                <snt-icon styleClasses={(this.expanded) ? "icon-icon_nav_minus_inv" : "icon-icon_nav_plus"}></snt-icon>
            </button>
        );

        const subnav = [
            <div class="position-relative">{[link, expander]}</div>
        ];

        const actionReference = (this.href) ? this.href.replace("action:", "") : "";
        const actionLink = <snt-action-link action-reference={actionReference} linkTitle={this.linkTitle} iconClassName={this.iconClassName}/>;

        const custom = [
            <slot/>
        ];


        const template = this.element.getAttribute("skipLink") ? <slot /> : {
            "link" : link,
            "action-link" : actionLink,
            "subnav" : subnav,
            "custom" : custom
        }
        return (
            <Host class={WebUtils.generateClasses([], this.styleClasses)} role="listitem">
                {template[this.type]}
            </Host>
        )
    }
}